import {useQuery} from '@tanstack/react-query'
import {useAuthUser} from 'react-auth-kit'
import useRequestParams from 'src/feature/bite/hook/request'
import {useNavigate, useParams, useSearchParams} from 'react-router-dom'

import {
  VIPListSchemaResponse,
  VIPSchemaResponse,
  VIPProgramSchemaResponse,
  VIPCampaignListSchemaResponse,
  VIPCampaignProductListSchemaResponse,
} from '../domain'
import {useVIP} from '../hook'
import {toast} from 'react-toastify'
import {CAMPAIGN_API} from 'src/feature/bite/domain'

const apiUrl = `${CAMPAIGN_API}/VIPTier`
const apiUrlProgram = `${CAMPAIGN_API}/VIPProgram`
const apiUrlCampaign = `${CAMPAIGN_API}/VIPTierCampaign`
const apiUrlCampaignProduct = `${CAMPAIGN_API}/CampaignProduct`
const apiUrlProduct = `${CAMPAIGN_API}/Product`
const apiCampaign = `${CAMPAIGN_API}/Campaign`
const apiCampaignFile = `${CAMPAIGN_API}/CampaignFile`
const apiCampaignProduct = `${CAMPAIGN_API}/CampaignProduct`

export function useGetVIPList() {
  const auth = useAuthUser()
  const {headers} = useRequestParams()
  const {VIPList, VIPListSet} = useVIP()

  const userID = auth()?.id

  return useQuery(
    ['allTiers', VIPList.paging.currentPageNumber, VIPList.paging.pageSize],
    async () => {
      const response = await fetch(apiUrl + '/GetAllVIPTier', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          userID,
          language: headers.Language,
          // storeID: '996225251718',
          paging: {
            currentPageNumber: VIPList.paging.currentPageNumber,
            pageSize: VIPList.paging.pageSize,
          },
        }),
      })
      const data = await response.json()

      const safeParse = VIPListSchemaResponse.safeParse(data)

      if (!safeParse.success) {
        throw new Error(safeParse.error.message)
      }

      const parse = VIPListSchemaResponse.parse(data)

      VIPListSet(parse)

      return true
    },
    {
      enabled: true,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: false,
      retry: false,
      onSuccess: () => {},
      onError: (error) => {
        console.log(error)
      },
    }
  )
}

export function useGetVIPCampaignList() {
  const auth = useAuthUser()
  const {VIPCampaignListSet} = useVIP()
  const {tierId} = useParams()
  const {headers} = useRequestParams()

  const userID = auth()?.id

  return useQuery(
    ['allVIPCampaigns'],
    async () => {
      const response = await fetch(apiUrlCampaign + '/GetAllVIPTierCampaign', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          // storeID,
          userID,
          vipTierID: tierId,
          language: headers.Language,
          paging: {
            currentPageNumber: 1,
            pageSize: 100,
          },
        }),
      })
      const data = await response.json()

      const safeParse = VIPCampaignListSchemaResponse.safeParse(data)

      if (!safeParse.success) {
        throw new Error(safeParse.error.message)
      }

      const parse = VIPCampaignListSchemaResponse.parse(data)

      VIPCampaignListSet(parse)

      return true
    },
    {
      enabled: true,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: false,
      retry: false,
      onSuccess: () => {},
      onError: (error) => {
        console.log(error)
      },
    }
  )
}

export function useGetDetail() {
  const auth = useAuthUser()
  const {headers, errorMessage, signOut} = useRequestParams()
  const {detailSet} = useVIP()
  const {tierId} = useParams()
  const router = useNavigate()

  const userID = auth()?.id

  return useQuery(
    ['vip', tierId],
    async () => {
      if (!tierId) return true

      const Response = await fetch(`${apiUrl}/GetVIPTier`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          userID,
          // storeID: '996225251718',
          language: headers.Language,
          vipTierID: tierId,
        }),
      })
      if (Response.status === 401) {
        signOut()
      }

      const Data = await Response.json()
      const safeParse = VIPSchemaResponse.safeParse(Data)
      const parse = VIPSchemaResponse.parse(Data)
      if (parse.result.isSuccess === false) {
        toast.error(parse.result.errorMessage || 'Error', {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
          theme: 'light',
        })
        return false
      }

      if (!Data.vipTierData.id) {
        router('/error/vip')
        return
      }

      if (!safeParse.success) {
        throw new Error(safeParse.error.message)
      }

      detailSet(parse.vipTierData)

      return true
    },
    {
      enabled: true,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: false,
      retry: false,
      onSuccess: () => {},
      onError: (error) => {
        toast.error(errorMessage, {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
          theme: 'light',
        })
      },
    }
  )
}

export function useGetProgramDetail() {
  const auth = useAuthUser()
  const {headers, errorMessage, signOut} = useRequestParams()
  const {programSet, prevProgramSet} = useVIP()
  const router = useNavigate()

  const userID = auth()?.id

  return useQuery(
    ['vipProgram'],
    async () => {
      const Response = await fetch(`${apiUrlProgram}/GetVIPProgram`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          userID,
          language: headers.Language,
          // storeID: '996225251718',
        }),
      })
      if (Response.status === 401) {
        signOut()
      }

      const Data = await Response.json()
      const safeParse = VIPProgramSchemaResponse.safeParse(Data)
      const parse = VIPProgramSchemaResponse.parse(Data)
      if (parse.result.isSuccess === false) {
        toast.error(parse.result.errorMessage || 'Error', {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
          theme: 'light',
        })
        return false
      }

      if (!Data.vipProgramData.id) {
        router('/error/vip')
        return
      }

      if (!safeParse.success) {
        throw new Error(safeParse.error.message)
      }

      programSet(parse.vipProgramData)
      prevProgramSet(parse.vipProgramData)

      return true
    },
    {
      enabled: true,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: false,
      retry: false,
      onSuccess: () => {},
      onError: (error) => {
        toast.error(errorMessage, {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
          theme: 'light',
        })
      },
    }
  )
}

export function useGetCampaignProducts() {
  const auth = useAuthUser()
  const {vipCampaignProductList, vipCampaignProductListSet, selectedCampaignProductSet} = useVIP()
  const {headers} = useRequestParams()
  const [params] = useSearchParams()
  const campaignId = params.get('campaignId') || '0'

  const userID = auth()?.id

  return useQuery(
    ['vipCampaignProductList', campaignId],
    async () => {
      if (!campaignId || !userID || campaignId === '0') return null
      if (vipCampaignProductList.data.length > 0) return null

      const response = await fetch(apiUrlCampaignProduct + '/GetAllCampaignProduct', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${auth()?.accessToken}`,
        },
        body: JSON.stringify({
          userID: userID,
          language: headers.Language,
          campaignId,
          filterList: [],
          hasProductOption: false,
          orderList: [],
          type: 1,
          paging: {
            currentPageNumber: 1,
            pageSize: 10,
          },
        }),
      })
      const data = await response.json()

      const safeParse = VIPCampaignProductListSchemaResponse.safeParse(data)

      if (!safeParse.success) {
        throw new Error(safeParse.error.message)
      }

      const parse = VIPCampaignProductListSchemaResponse.parse(data)

      selectedCampaignProductSet(parse.data)
      vipCampaignProductListSet(parse)

      return true
    },
    {
      enabled: true,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: false,
      retry: false,
      onSuccess: () => {},
      onError: (error) => {
        console.log(error)
      },
    }
  )
}

// export function useGetCampaignProductsAll({campaignId}: {campaignId: string}) {
//   const auth = useAuthUser()
//   const {vipCampaignProductListAllSet} = useVIP()

//   const userID = auth()?.id

//   return useQuery(
//     ['campaignProductsAll'],
//     async () => {
//       const response = await fetch(apiUrlProduct + '/GetAllProduct', {
//         method: 'POST',
//         headers: {
//           'Content-Type': 'application/json',
//           Authorization: `Bearer ${auth()?.accessToken}`,
//         },
//         body: JSON.stringify({
//           userID: userID,
//           campaignId,
//           filterList: [],
//           hasProductOption: false,
//           orderList: [],
//           type: 1,
//           paging: {
//             currentPageNumber: 1,
//             pageSize: 10,
//           },
//         }),
//       })
//       const data = await response.json()

//       const safeParse = VIPCampaignProductListSchemaResponse.safeParse(data)

//       if (!safeParse.success) {
//         throw new Error(safeParse.error.message)
//       }

//       const parse = VIPCampaignProductListSchemaResponse.parse(data)

//       vipCampaignProductListAllSet({
//         ...parse,
//         // response: {
//         //   dataList: parse.response.dataList,
//         //   paging: {
//         //     currentPageNumber: parse.response.paging.currentPageNumber,
//         //     totalPagesNumber: parse.response.paging.totalPagesNumber,
//         //     pageSize: parse.response.paging.pageSize,
//         //     totalCount: parse.response.paging.totalCount,
//         //   },
//         // },
//         // responseResult: {
//         //   result: parse.responseResult.result,
//         //   exceptionDetail: parse.responseResult.exceptionDetail,
//         // },
//       })

//       return true
//     },
//     {
//       enabled: true,
//       refetchOnWindowFocus: true,
//       refetchOnMount: true,
//       refetchOnReconnect: true,
//       cacheTime: 0,
//       staleTime: 0,
//       onSuccess: () => {},
//       onError: (error) => {
//         console.log(error)
//       },
//     }
//   )
// }

export function useGetCampaignProductsAll({filterList}: {filterList: any[]}) {
  const auth = useAuthUser()
  const {vipCampaignProductListAll, vipCampaignProductListAllSet} = useVIP()
  const {headers} = useRequestParams()
  const userID = auth()?.id

  return useQuery(
    [
      'campaignProductsAll',
      filterList,
      vipCampaignProductListAll.paging.currentPageNumber,
      vipCampaignProductListAll.paging.pageSize,
    ],
    async () => {
      if (!userID) return null

      const response = await fetch(apiUrlProduct + '/GetAllProduct', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${auth()?.accessToken}`,
        },
        body: JSON.stringify({
          userID: userID,
          language: headers.Language,
          filterList,
          campaignID: '',
          hasProductOption: false,
          orderList: [],
          type: 1,
          paging: vipCampaignProductListAll.paging,
        }),
      })
      const data = await response.json()

      const safeParse = VIPCampaignProductListSchemaResponse.safeParse(data)

      if (!safeParse.success) {
        throw new Error(safeParse.error.message)
      }

      const parse = VIPCampaignProductListSchemaResponse.parse(data)

      vipCampaignProductListAllSet(parse)

      return true
    },
    {
      enabled: true,
      retry: false,
      onSuccess: () => {},
      onError: (error) => {},
    }
  )
}

export function useGetRewardDetail() {
  const {headers, errorMessage, signOut} = useRequestParams()
  const auth = useAuthUser()
  const [params] = useSearchParams()
  const campaignID = params.get('campaignId')

  const userID = auth()?.id

  return useQuery(
    ['referralReward', campaignID],
    async () => {
      if (!campaignID || !userID || campaignID === '0') return null

      const Response = await fetch(`${apiCampaign}/GetCampaign`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          userID,
          language: headers.Language,
          campaignID,
        }),
      })

      if (Response.status === 401) {
        signOut()
      }

      const Data = await Response.json()

      return Data
    },
    {
      enabled: true,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: false,
      retry: false,
      onSuccess: () => {},
      onError: (error) => {
        toast.error(errorMessage, {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
          progress: undefined,
          theme: 'light',
        })
      },
    }
  )
}

export function useGetCampaignFiles({campaignId}: {campaignId: string}) {
  const auth = useAuthUser()
  const {headers} = useRequestParams()
  const {campaignFilesSet, uploadProductsSet} = useVIP()

  const userID = auth()?.id

  return useQuery(
    ['campaignFiles', campaignId],
    async () => {
      if (!campaignId || !userID || campaignId === '0') return null

      const response = await fetch(apiCampaignFile + '/GetAllCampaignFile', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          userID: userID,
          language: headers.Language,
          campaignId,
          paging: {
            currentPageNumber: 1,
            pageSize: 100,
          },
        }),
      })
      const data = await response.json()

      const fileIDList = data.data.map((file) => file.id)
      const firstFile = data.data[0]

      const ResponseProduct = await fetch(`${apiCampaignProduct}/GetAllCampaignProduct`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          userID: auth().id,
          language: headers.Language,
          campaignID: '',
          fileIDList,
          paging: {
            currentPageNumber: 1,
            pageSize: 100,
          },
        }),
      })

      const DataProduct = await ResponseProduct.json()

      const productsAddType = DataProduct.data.map((product) => {
        return {
          ...product,
          type: firstFile?.type || 1,
        }
      })

      campaignFilesSet(data.data)
      uploadProductsSet(productsAddType || [])

      return data
    },
    {
      enabled: true,
      onSuccess: () => {},
      onError: (error) => {},
    }
  )
}
